import { DateUtils } from '../../utils/DateUtils';
import { TagVariant } from '../../components/Tag/Tag';
import i18n from '../../i18n/config';
import { SurveyFrequency } from '../../api/types/survey-enums';

export type SurveyType = 'standard' | 'custom';

/**
 * Scheduled survey with type information for display
 */
export interface ScheduledSurvey {
  readonly id: number;
  readonly title: string;
  readonly scheduledTime: Date | string;
  readonly frequency?: string | null;
  readonly type: SurveyType;
}

/**
 * Base survey interface for standard surveys
 */
export interface PlannedSurvey {
  readonly id: number;
  readonly title: string;
  readonly scheduledTime: Date | string;
  readonly frequency?: string | null;
}

/**
 * Base survey interface for custom surveys
 */
export interface ScheduledCustomSurvey {
  readonly id: number;
  readonly title: string;
  readonly scheduledTime: Date | string;
  readonly frequency?: string | null;
}

/**
 * Get frequency labels from i18n
 */
function getFrequencyLabels(): Record<string, string> {
  return {
    [SurveyFrequency.WEEKLY]: i18n.t('surveys.scheduledSurvey.frequencies.weekly'),
    [SurveyFrequency.EVERY_OTHER_WEEK]: i18n.t('surveys.scheduledSurvey.frequencies.everyOtherWeek'),
    [SurveyFrequency.EVERY_THIRD_WEEK]: i18n.t('surveys.scheduledSurvey.frequencies.everyThirdWeek'),
    [SurveyFrequency.MONTHLY]: i18n.t('surveys.scheduledSurvey.frequencies.monthly'),
    [SurveyFrequency.EVERY_OTHER_MONTH]: i18n.t('surveys.scheduledSurvey.frequencies.everyOtherMonth'),
  };
}

function getDefaultFrequencyLabel(): string {
  return i18n.t('surveys.scheduledSurvey.frequencies.doesNotRepeat');
}

/**
 * Presenter for scheduled/planned surveys
 *
 * Handles:
 * - Merging and sorting of standard and custom surveys
 * - Type label and variant mapping
 * - Date/time formatting
 * - Frequency label mapping
 */
export class ScheduledSurveyPresenter {
  /**
   * Merge standard and custom surveys into a single sorted list
   *
   * Surveys are sorted by scheduledTime (earliest first)
   */
  static mergeAndSort(
    standardSurveys: PlannedSurvey[] = [],
    customSurveys: ScheduledCustomSurvey[] = [],
  ): ScheduledSurvey[] {
    const surveysWithType: ScheduledSurvey[] = [
      ...standardSurveys.map((s) => ({ ...s, type: 'standard' as SurveyType })),
      ...customSurveys.map((s) => ({ ...s, type: 'custom' as SurveyType })),
    ];

    return surveysWithType.sort(
      (a, b) =>
        new Date(a.scheduledTime).getTime() -
        new Date(b.scheduledTime).getTime(),
    );
  }

  /**
   * Get display label for survey type
   *
   * Returns:
   * - "Egen"/"Custom" for custom surveys
   * - "Standard" for standard surveys
   */
  static getTypeLabel(type: SurveyType): string {
    return type === 'custom'
      ? i18n.t('surveys.scheduledSurvey.types.custom')
      : i18n.t('surveys.scheduledSurvey.types.standard');
  }

  /**
   * Get tag variant for survey type
   *
   * Returns:
   * - 'success' for custom surveys
   * - 'info' for standard surveys
   */
  static getTypeVariant(type: SurveyType): TagVariant {
    return type === 'custom' ? 'success' : 'info';
  }

  /**
   * Format scheduled date/time for display
   *
   * Format: "DD.MM.YYYY klokken/at HH:MM"
   * Example: "15.05.2024 klokken 09:00" (Norwegian)
   * Example: "15.05.2024 at 09:00" (English)
   */
  static formatScheduledDate(dateString: string): string {
    const date = new Date(dateString);
    const shortDate = DateUtils.getShortDateFromDate(date, true);
    const time = DateUtils.getTimeFromDate(date);
    return `${shortDate} ${i18n.t('surveys.scheduledSurvey.atTime')} ${time}`;
  }

  /**
   * Get frequency label for display
   *
   * Returns:
   * - Translated frequency label if frequency exists
   * - "Gjentas ikke."/"Does not repeat." if no frequency
   */
  static getFrequencyLabel(frequency?: string | null): string {
    if (!frequency) {
      return getDefaultFrequencyLabel();
    }
    const labels = getFrequencyLabels();
    return labels[frequency] ?? getDefaultFrequencyLabel();
  }
}
